// Escalation level is DERIVED from time, not stored/forwarded. A complaint has
// a single reference time (when it entered In Progress / was dispatched — the
// 72h clock start). The current level is a pure function of how many hours have
// elapsed since then, against cumulative thresholds. This is computed the same
// way for past and future data, so nothing needs migrating.

// Display-only status relabel (DB still stores "Closed").
import { statusLabel } from './displayText';

const readHours = (key, fallback) => {
  const v = parseFloat(process.env[key]);
  return Number.isFinite(v) && v > 0 ? v : fallback;
};

// Cumulative hours since the reference at which each level begins. Three levels
// only: DCF -> CF -> CCF. CCF (Chief) is the terminal level ("Super Escalated")
// — there is NO Administration level. Must mirror the backend values.
const _DCF_H = readHours('REACT_APP_ESCALATE_DCF_HOURS', 72);
const _CF_H = readHours('REACT_APP_ESCALATE_CF_HOURS', 120);
const _CCF_H = readHours('REACT_APP_ESCALATE_CCF_HOURS', 168);
export const ESC_THRESHOLDS = {
  DCF: _DCF_H,
  Conservator: _CF_H,
  Chief: _CCF_H,
};

export const LEVEL_CODE = {
  DCF: 'DCF',
  Conservator: 'CF',
  Chief: 'CCF',
};

export const LEVEL_LABEL = {
  DCF: 'DCF (Division)',
  Conservator: 'Conservator (CF)',
  Chief: 'Chief Conservator (CCF)',
};

export const LEVEL_ORDER = ['DCF', 'Conservator', 'Chief'];

// Best-effort level extraction from a remark (legacy history rows / fallbacks).
export const parseEscalationLevelFromRemark = (remark = '') => {
  const r = String(remark).toLowerCase();
  // Legacy "administration" rows collapse onto the terminal Chief level.
  if (r.includes('administration')) return 'Chief';
  if (r.includes('chief')) return 'Chief';
  if (r.includes('conservator')) return 'Conservator';
  if (r.includes('dcf')) return 'DCF';
  return null;
};

// The STARTING level of a complaint's escalation ladder = the hierarchy level of
// the officer it was dispatched to. A complaint handed to a DCF starts at DCF
// (ladder DCF -> CF -> CCF); one handed to a CF starts at CF (ladder CF -> CCF,
// NEVER DCF); one handed to a CCF stays at CCF. Prefers the stored
// escalation.startLevel; falls back to the assigned officer's designation (works
// for complaints dispatched before startLevel was recorded).
export const baseLevel = (complaint) => {
  if (!complaint) return 'DCF';
  const stored = complaint.escalation?.startLevel;
  if (stored && LEVEL_ORDER.includes(stored)) return stored;

  const inProgress = (complaint.statusHistory || []).find(
    (e) => (e.status || '').toLowerCase() === 'in progress'
  );
  const designation = String(
    complaint.assigned_to?.designation || inProgress?.assignedTo || ''
  ).toLowerCase();

  // Check Chief before Conservator ("chief conservator" contains "conservator").
  if (designation.includes('chief') || /\bccf\b/.test(designation) || designation.includes('zone')) {
    return 'Chief';
  }
  if (designation.includes('conservator') || /\bcf\b/.test(designation) || designation.includes('circle')) {
    return 'Conservator';
  }
  if (
    designation.includes('dfo') ||
    designation.includes('dcf') ||
    designation.includes('division') ||
    designation.includes('district')
  ) {
    return 'DCF';
  }
  return 'DCF';
};

// How many ladder steps the elapsed time has crossed (0, 1 or 2), independent of
// the starting level. The clock always starts at dispatch, so the 1st step lands
// at ESCALATE_DCF_HOURS, the 2nd at the CF threshold, the 3rd at the CCF one.
const bandIndexForElapsedHours = (h) => {
  if (h == null) return 0;
  if (h >= ESC_THRESHOLDS.Chief) return 2;
  if (h >= ESC_THRESHOLDS.Conservator) return 1;
  return 0;
};

// The single anchor time. The escalation clock starts at DISPATCH (entering
// In Progress), NEVER at creation — a complaint can sit "Open" for days before
// being dispatched, so anchoring to time_of_call / createdAt would inflate the
// level of older complaints. Order: explicit referenceAt -> dispatched_time ->
// In-Progress history row -> reconstructed from the Escalated moment.
export const escalationReferenceTime = (complaint) => {
  if (!complaint) return null;
  const pick = (v) => (v ? new Date(v) : null);
  if (complaint.escalation?.referenceAt) return pick(complaint.escalation.referenceAt);
  const disp = pick(complaint.dispatched_time);
  if (disp) return disp;
  const ip = (complaint.statusHistory || []).find((e) => e.status === 'In Progress');
  if (ip?.changedAt) return pick(ip.changedAt);
  const escRows = (complaint.statusHistory || []).filter((e) => e.status === 'Escalated');
  const esc = escRows[escRows.length - 1];
  if (esc?.changedAt) return new Date(new Date(esc.changedAt).getTime() - ESC_THRESHOLDS.DCF * 3.6e6);
  return null;
};

export const elapsedHoursSinceRef = (complaint) => {
  const ref = escalationReferenceTime(complaint);
  if (!ref || Number.isNaN(ref.getTime())) return null;
  return (Date.now() - ref.getTime()) / 3.6e6;
};

// Derive the level from an elapsed-hours value alone.
export const levelForElapsedHours = (h) => {
  if (h == null) return null;
  if (h >= ESC_THRESHOLDS.Chief) return 'Chief'; // terminal — "Super Escalated"
  if (h >= ESC_THRESHOLDS.Conservator) return 'Conservator';
  return 'DCF'; // it is Escalated, so at least DCF
};

// Current derived level of an escalated complaint (null if not escalated).
// Offsets the elapsed-time band by the complaint's starting level, so a
// CF-assigned complaint reads CF then CCF (never DCF).
export const currentEscalationLevel = (complaint) => {
  if (!complaint || complaint.status !== 'Escalated') return null;
  const baseIdx = LEVEL_ORDER.indexOf(baseLevel(complaint));
  const band = bandIndexForElapsedHours(elapsedHoursSinceRef(complaint));
  const idx = Math.min(baseIdx + band, LEVEL_ORDER.length - 1);
  return LEVEL_ORDER[idx] || 'DCF';
};

// Same, but from a plain reference time + status (e.g. map payloads).
export const levelFromReference = (status, referenceAt) => {
  if (status !== 'Escalated' || !referenceAt) return null;
  const ref = new Date(referenceAt);
  if (Number.isNaN(ref.getTime())) return 'DCF';
  return levelForElapsedHours((Date.now() - ref.getTime()) / 3.6e6);
};

// Remark for each derived step — states the REAL threshold (from env), so the
// timeline can never show a stale/wrong number (e.g. an old "48 hours" baked
// into historic statusHistory).
export const forwardRemark = (level) => {
  const h = Math.round(ESC_THRESHOLDS[level]);
  switch (level) {
    case 'DCF':
      return `Escalated at DCF level after ${h} hours without closure.`;
    case 'Conservator':
      return `Automatically forwarded to Conservator (CF) after ${h} hours without closure.`;
    case 'Chief':
      return `Super Escalated to Chief Conservator (CCF) after ${h} hours without closure.`;
    default:
      return '';
  }
};

// Every level the complaint has reached, DERIVED THE SAME WAY as the badge so
// the timeline and the datatable can never contradict. An escalated complaint is
// always at least DCF, so DCF is always included; higher levels are added up to
// the current derived level. The DCF step is timed to the real first-escalation
// moment (its statusHistory row) so it never appears in the future.
export const escalationStepsCrossed = (complaint) => {
  if (!complaint || complaint.status !== 'Escalated') return [];
  const ref = escalationReferenceTime(complaint);
  const baseIdx = LEVEL_ORDER.indexOf(baseLevel(complaint));
  const currentIdx = LEVEL_ORDER.indexOf(currentEscalationLevel(complaint) || LEVEL_ORDER[baseIdx]);
  const escRow = (complaint.statusHistory || []).find((e) => e.status === 'Escalated');
  const escAt = escRow?.changedAt ? new Date(escRow.changedAt) : null;

  const steps = [];
  // k is the step offset from the starting level. The kth step always lands at
  // the kth cumulative threshold (DCF_H, CF_H, CCF_H), regardless of the level
  // its officer actually sits at — so a CF-started ladder's 1st step is CF at
  // ESCALATE_DCF_HOURS, its 2nd is CCF at the CF threshold.
  for (let k = 0; baseIdx + k <= currentIdx; k++) {
    const lvl = LEVEL_ORDER[baseIdx + k];
    const thresholdKey = LEVEL_ORDER[k];
    const hours = Math.round(ESC_THRESHOLDS[thresholdKey]);
    let at = ref ? new Date(ref.getTime() + ESC_THRESHOLDS[thresholdKey] * 3.6e6) : escAt;
    if (at && at.getTime() > Date.now()) {
      at = escAt && escAt.getTime() <= Date.now() ? escAt : new Date();
    }
    const remark =
      k === 0
        ? `Escalated at ${LEVEL_CODE[lvl]} level after ${hours} hours without closure.`
        : lvl === 'Chief'
        ? `Super Escalated to Chief Conservator (CCF) after ${hours} hours without closure.`
        : `Automatically forwarded to ${LEVEL_LABEL[lvl]} after ${hours} hours without closure.`;
    steps.push({ level: lvl, at, remark });
  }
  return steps;
};

// A complaint that has reached the terminal Chief (CCF) level is "Super Escalated".
export const isSuperEscalated = (complaint) => currentEscalationLevel(complaint) === 'Chief';

// "Escalated · CF" for an escalated complaint, otherwise the plain status.
export const statusWithLevel = (complaint) => {
  if (!complaint) return '';
  if (complaint.status === 'Escalated') {
    const lvl = currentEscalationLevel(complaint);
    return lvl ? `Escalated · ${LEVEL_CODE[lvl] || lvl}` : 'Escalated';
  }
  return statusLabel(complaint.status);
};

// From a plain status + level pair (level already derived).
export const labelForStatus = (status, level) => {
  if (status === 'Escalated' && level) return `Escalated · ${LEVEL_CODE[level] || level}`;
  return statusLabel(status);
};
